import { formatMoney } from '../../i18n/formatNumber';
import React, { useState } from "react";
import { Modal, Button, Table } from "react-bootstrap";
import * as PropTypes from "prop-types";

import {
  TEACHER_COSTS,
} from "../common/Const";
import { getClassTypeName } from "../common/Helpers";
import { getClientLessonPricing } from "../common/clientLessonPricing";
import { DEFAULT_LESSON_TYPES_SETTING, isGroupReportLessonType } from "../common/lessonTypes";
import { useTranslation } from 'react-i18next';

const AttendanceReportPaymentModal = ({ selected, forClient = false, lessonTypes }) => {
  const { t: tr, i18n } = useTranslation();
  const [toggle, setToggle] = useState(false);

  const handleToggle = () => setToggle(prevToggle => !prevToggle);

  const calculateTeacherClassCost = (reg, classType) => {
    const maxMembers = reg.maxMembers || 0;
    const presentCount = reg.presentCount || 0;
    const costPerClass = TEACHER_COSTS[classType] || 0;

    if (isGroupReportLessonType(classType, lessonTypes)) {
      return parseFloat(
        ((costPerClass / maxMembers) * presentCount).toFixed(2)
      );
    }
    return costPerClass;
  };

  const organizeDataByClassType = (data) => {
    const classTypeData = {};

    data.forEach((reg) => {
      const { classType } = reg;
      const presentCount = reg.presentCount || 0;
      const key = `${classType}-${presentCount}`;

      if (!classTypeData[key]) {
        classTypeData[key] = {
          classType,
          count: 0,
          presentCount,
          totalCost: 0,
        };
      }

      const costPerClass = forClient
        ? getClientLessonPricing(classType, presentCount, reg.date).rate || 0
        : calculateTeacherClassCost(reg, classType);

      classTypeData[key].count += 1;
      classTypeData[key].totalCost += costPerClass;
    });

    return classTypeData;
  };

  const organizeDataByClientAndStudent = (data) => {
    const clientData = {};

    data.forEach((reg) => {
      reg.students.forEach((student) => {
        const {clientName, studentName} = student;
        const { classType, rate } = getClientLessonPricing(reg.classType, reg.presentCount || 0, reg.date);

        if (!clientData[clientName]) {
          clientData[clientName] = {};
        }
        if (!clientData[clientName][studentName]) {
          clientData[clientName][studentName] = {};
        }
        if (!clientData[clientName][studentName][classType]) {
          clientData[clientName][studentName][classType] = {
            count: 0,
            totalCost: 0,
          };
        }

        const costPerClass = rate || 0;
        clientData[clientName][studentName][classType].count += 1;
        clientData[clientName][studentName][classType].totalCost +=
          costPerClass;
      });
    });

    return clientData;
  };

  const calculateTotalCost = data => Object.values(data).reduce((sum, item) => {
    if (forClient) {
      return (
        sum +
          Object.values(item).reduce(
            (studentSum, classTypes) => Object.values(classTypes).reduce(
              (classSum, { totalCost: classTypeTotalCost }) => classSum + classTypeTotalCost,
              0
            ),
            0
          )
      );
    }
    return sum + item.totalCost;
  }, 0);

  const calculateClientTotal = studentData => Object.values(studentData).reduce((clientSum, classes) => (
    clientSum +
        Object.values(classes).reduce(
          (classSum, { totalCost: classTypeTotalCost }) => classSum + classTypeTotalCost,
          0
        )
  ), 0);

  const organizedData = forClient
    ? organizeDataByClientAndStudent(selected)
    : organizeDataByClassType(selected);
  const totalCost = parseFloat(calculateTotalCost(organizedData).toFixed(2));

  const sortedData = !forClient
    ? Object.entries(organizedData).sort(([, dataA], [, dataB]) => {
      const nameA = getClassTypeName(dataA.classType, lessonTypes).toLowerCase();
      const nameB = getClassTypeName(dataB.classType, lessonTypes).toLowerCase();
      return nameA.localeCompare(nameB);
    })
    : null;

  let modalBody;

  if (selected.length > 0) {
    if (forClient) {
      modalBody = Object.entries(organizedData).map(([clientName, studentData]) => (
        <div key={clientName} className="client-section">
          <h5 className="client-name report-modal-title mb-3">{tr("billing:client_87")} {clientName}
          </h5>
          <Table className="mb-4" bordered>
            <thead>
              <tr>
                <th>{tr("billing:studentsFirstName_88")}</th>
                <th>{tr("manage:lessonType_196")}</th>
                <th>{tr("billing:quantity_89")}</th>
                <th>{tr("billing:total_90")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(studentData).map(
                ([studentName, classes], index) => (
                  <React.Fragment key={studentName}>
                    <tr
                      className={`student-row ${
                        index % 2 === 0 ? "" : "report-modal-table-row"
                      }`}
                    >
                      <td colSpan="4" className="report-modal-title">
                        {studentName}
                      </td>
                    </tr>
                    {Object.entries(classes).map(
                      ([classType, { count, totalCost: classTypeTotalCost }]) => (
                        <tr
                          key={`${studentName}-${classType}`}
                          className={`class-type-row ${
                            index % 2 === 0 ? "" : "report-modal-table-row"
                          }`}
                        >
                          <td />
                          <td>{getClassTypeName(classType, lessonTypes)}</td>
                          <td>{count}</td>
                          <td>{formatMoney(classTypeTotalCost, i18n.language)}</td>
                        </tr>
                      )
                    )}
                  </React.Fragment>
                )
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" className="font-weight-bold text-right">{tr("billing:totalPaymentsForClient_92")}</td>
                <td className="font-weight-bold">
                  {formatMoney(calculateClientTotal(studentData), i18n.language)}</td>
              </tr>
            </tfoot>
          </Table>
        </div>
      ));
    } else {
      modalBody = (
        <Table className="mb-4" bordered>
          <thead>
            <tr>
              <th>{tr("manage:lessonType_196")}</th>
              <th>{tr("billing:quantity_89")}</th>
              <th>{tr("billing:present_93")}</th>
              <th>{tr("billing:total_90")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map(
              ([key, { classType, count, presentCount, totalCost: classTypeTotalCost }]) => (
                <tr key={key}>
                  <td>{getClassTypeName(classType, lessonTypes)}</td>
                  <td>{count}</td>
                  <td>{presentCount}</td>
                  <td>{formatMoney(classTypeTotalCost, i18n.language)}</td>
                </tr>
              )
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3" className="font-weight-bold text-right">{tr("billing:totalPaymentsForTeacher_94")}</td>
              <td className="font-weight-bold">{formatMoney(totalCost, i18n.language)}</td>
            </tr>
          </tfoot>
        </Table>
      );
    }
  } else {
    modalBody = tr("manage:extraNoLessonsSelected_102");
  }

  return (
    <>
      <Button
        className={`btn ${
          forClient ? "btn-secondary" : "btn-primary"
        } manage-button btn-block h-100 p-1`}
        variant="primary"
        onClick={handleToggle}
      >{tr("billing:calculatePayment_95")}{tr(forClient ? 'manage:client_108' : 'manage:teacher_9')})
      </Button>

      <Modal size="lg" show={toggle} onHide={handleToggle} centered>
        <Modal.Header closeButton closeLabel={tr("common:close")}>
          <Modal.Title className="font-weight-bold">{tr(forClient ? 'billing:clientPayment' : 'billing:teacherPayment')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalBody}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="manage-button manage-red-button btn btn-primary"
            onClick={handleToggle}
          >{tr("common:close")}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

AttendanceReportPaymentModal.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    dayOfWeek: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    classType: PropTypes.string.isRequired,
    teacherName: PropTypes.string.isRequired,
    presentCount: PropTypes.number.isRequired,
    maxMembers: PropTypes.number.isRequired,
    students: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      isPresent: PropTypes.bool,
      clientId: PropTypes.string.isRequired,
      clientName: PropTypes.string.isRequired,
      included: PropTypes.bool.isRequired,
      studentName: PropTypes.string.isRequired,
      comment: PropTypes.string.isRequired,
    })),
  })).isRequired,
  forClient: PropTypes.bool,
  lessonTypes: PropTypes.shape({
    id: PropTypes.string,
    types: PropTypes.array,
  }),
};

AttendanceReportPaymentModal.defaultProps = {
  forClient: false,
  lessonTypes: DEFAULT_LESSON_TYPES_SETTING,
};

export default AttendanceReportPaymentModal;
